import ConditionalRender from 'base/ConditionalRender/ConditionalRender';
import { StandardButton } from 'base/StandardButton/StandardButton';
import SearchBox from 'components/common/SearchBox';
import { ADMIN_LEVEL_FORM, ADMIN_LEVEL_FORM_ID } from 'constant/RoutesUrls';
import { t } from 'i18next';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '../../redux/HooksRedux';
import { RootState } from 'redux/rootReducer';
import {
  ACCENDING,
  DESCENDING,
  ENGLISH,
  modules,
  SELECTED_LANGUAGE,
  ViewAllLimit
} from 'utils/enums/misc';
import { capitalizeFirstLetter, checkAccess } from 'utils/helpers';
import {
  AdminLevelPostI,
  paramsI,
  SingleAdminLevelI
} from '../../../types/interface';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { getCookie } from 'utils/cookieUtils';
import * as adminLevelAction from '../../redux/AdminLevel/AdminLevelAction';
import {
  errorNotification,
  successNotification
} from 'base/notifications/Notifications';
import { debounceTime } from 'utils/staticData';
import useAdvanceTable from 'hooks/useAdvanceTable';
import DeleteModel from 'base/DeleteModal/DeleteModal';
import { StandardLoader } from 'base/Loader/Loader';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import CustomAdvanceTableFooter from 'components/customComponent/CustomAdvanceTableFooter';
import { Dropdown } from 'react-bootstrap';
import { UilEllipsisV } from '@iconscout/react-unicons';
import ViewAdminLevelModal from './ViewAdminLevelModal';

const AdminLevel = () => {
  const [searchText, setSearchText] = useState<string>('');
  const [pageCount, setPageCount] = useState<number>(1);
  const [isAllVisibleData, setIsAllVisibleData] = useState(false);
  const [limit, setLimit] = useState<number>(10);
  const [allAdminLevel, setAllAdminLevel] = useState<AdminLevelPostI[]>([]);
  const [singleAdminLevel, setSingleAdminLevel] = useState<SingleAdminLevelI>();
  const [isLoading, setIsLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>();
  const [deleteAdminLevelData, setDeleteAdminLevelData] = useState<{
    admin_level_id: number;
    showDeletePopup: boolean;
  }>({
    admin_level_id: 0,
    showDeletePopup: false
  });
  const [viewAdminLevelData, setViewAdminLevelData] = useState<{
    admin_level_id: number;
    showViewPopup: boolean;
  }>({
    admin_level_id: 0,
    showViewPopup: false
  });

  const userPermission = useSelector(
    (state: RootState) => state.userProfile.permission
  );
  const { totalPages, totalRecords } = useSelector(
    (state: RootState) => state.adminLevelSlice || 0
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentLanguage = getCookie(SELECTED_LANGUAGE);

  const getAllAdminLevels = async () => {
    const params: paramsI = {
      page: pageCount,
      limit: isAllVisibleData ? ViewAllLimit : limit,
      keyword: searchText,
      sortBy: sorting?.[0]?.id,
      sortOrder: sorting?.[0]?.desc ? DESCENDING : ACCENDING
    };
    setIsLoading(true);
    try {
      const response = await dispatch(adminLevelAction.getAdminLevels(params));

      if (response?.status === 200) {
        setAllAdminLevel(response.data?.data || []);
        if (pageCount > response?.data?.pagination?.totalPages) {
          setPageCount(1);
        }
      } else {
        errorNotification(response?.data?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getSingleAdminLevel = async (id: number) => {
    try {
      const response = await dispatch(
        adminLevelAction.getSingleAdminLevel(id)
      );
      if (response?.status === 200) {
        setSingleAdminLevel(response.data?.data);
        setViewAdminLevelData({
          admin_level_id: id,
          showViewPopup: true
        });
      } else {
        errorNotification(response?.data?.message);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleEdit = (editAdminLevelId: number) => {
    console.log(editAdminLevelId);

    navigate(
      `${ADMIN_LEVEL_FORM}?editAdminLevelId=${editAdminLevelId?.toString()}`
    );
  };

  const debouncedSearch = useCallback(() => {
    const handler = setTimeout(() => {
      getAllAdminLevels();
    }, debounceTime);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText, pageCount, isAllVisibleData, sorting, limit]);

  useEffect(() => {
    setIsLoading(true);
    const cleanup = debouncedSearch();
    return cleanup;
  }, [searchText, pageCount, isAllVisibleData, sorting, limit]);

  const handleDeleteAdminLevel = async () => {
    try {
      const response = await dispatch(
        adminLevelAction.deleteAdminLevel(deleteAdminLevelData.admin_level_id)
      );
      if (response?.status === 200) {
        successNotification(response.data?.message);
        setDeleteAdminLevelData({ admin_level_id: 0, showDeletePopup: false });
        getAllAdminLevels();
      } else {
        errorNotification(response?.data?.message);
      }
    } catch (error) {
      setDeleteAdminLevelData({ admin_level_id: 0, showDeletePopup: false });
      console.log(error);
    }
  };

  const renderDeletePopup = () => {
    if (deleteAdminLevelData.showDeletePopup) {
      return (
        <DeleteModel
          onCancel={() =>
            setDeleteAdminLevelData({
              admin_level_id: 0,
              showDeletePopup: false
            })
          }
          onConfirm={handleDeleteAdminLevel}
        />
      );
    }
  };

  const renderViewAdminLevel = () => {
    if (viewAdminLevelData.showViewPopup) {
      return (
        <ViewAdminLevelModal
          show={viewAdminLevelData.showViewPopup}
          setShow={() =>
            setViewAdminLevelData({ admin_level_id: 0, showViewPopup: false })
          }
          data={singleAdminLevel}
        />
      );
    }
  };

  const columns: ColumnDef<AdminLevelPostI>[] = [
    {
      id: 'admin_level_number',
      accessorKey: t('admin_level.table.column_1'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('applications.table.title_1')}</span>
      ),
      cell: ({ row: { index } }) => <div className="py-2">{index + 1}</div>
    },
    {
      id: 'admin_level_name',
      accessorKey: t('admin_level.table.column_2'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('admin_level.table.column_2')}</span>
      ),
      cell: ({ row }) => (
        <div className="py-2">
          {currentLanguage === ENGLISH
            ? row.original?.level_name_en
            : row.original?.level_name_de || '-'}
        </div>
      )
    },
    {
      id: 'admin_level_order',
      accessorKey: t('admin_level.table.column_3'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('admin_level.table.column_3')}</span>
      ),
      cell: ({ row }) => (
        <div className="py-2">{row.original?.level_order || '-'}</div>
      )
    },
    {
      id: 'admin_level_user_count',
      accessorKey: t('admin_level.table.column_4'),
      enableSorting: false,
      header: () => (
        <span className="text-nowrap">{t('admin_level.table.column_4')}</span>
      ),
      cell: ({ row }) => (
        <div className="py-2">{row.original?.assigned_user_count || '0'}</div>
      )
    },
    {
      id: 'action',
      accessorKey: checkAccess([modules.ADMIN_LEVEL.view], userPermission)
        ? t('admin_level.table.column_5')
        : '',
      enableSorting: false,
      header: () =>
        checkAccess([modules.ADMIN_LEVEL.view], userPermission)
          ? t('admin_level.table.column_5')
          : '',
      cell: ({ row }) => (
        <div className="py-2">
          <ConditionalRender
            condition={checkAccess([modules.ADMIN_LEVEL.view], userPermission)}
          >
            <StandardButton
              buttonText={t('admin_level.table.button.text_1')}
              onClick={() => getSingleAdminLevel(row.original?.level_order)}
            />
          </ConditionalRender>
        </div>
      )
    },
    {
      id: 'action',
      accessorKey: checkAccess(
        [modules.ADMIN_LEVEL.edit, modules.ADMIN_LEVEL.delete],
        userPermission
      )
        ? t('general.common_text.text_8')
        : '',
      enableSorting: false,
      header: () =>
        checkAccess(
          [modules.ADMIN_LEVEL.edit, modules.ADMIN_LEVEL.delete],
          userPermission
        ) ? (
          <span className="text-nowrap">{t('general.common_text.text_8')}</span>
        ) : (
          ''
        ),
      cell: ({ row: { original } }) => (
        <ConditionalRender
          condition={checkAccess(
            [modules.ADMIN_LEVEL.edit, modules.ADMIN_LEVEL.delete],
            userPermission
          )}
        >
          <Dropdown
            id="scroll_in_table"
            placement="auto"
            className="position-static"
          >
            <Dropdown.Toggle
              variant="link"
              className="no-padding no-margin ps-0 d-flex"
            >
              <UilEllipsisV className="text-body" />
            </Dropdown.Toggle>

            <Dropdown.Menu className="p-0">
              <ConditionalRender
                condition={checkAccess(
                  [modules.ADMIN_LEVEL.edit],
                  userPermission
                )}
              >
                <Dropdown.Item
                  className="pe-0 border-bottom"
                  onClick={() => handleEdit(original?.level_order)}
                >
                  <span className="d-flex fs-16 p-0 font-normal">
                    <p className="mb-0">
                      {capitalizeFirstLetter(t('general.common_text.text_13'))}
                    </p>
                  </span>
                </Dropdown.Item>
              </ConditionalRender>
              <ConditionalRender
                condition={checkAccess(
                  [modules.ADMIN_LEVEL.delete],
                  userPermission
                )}
              >
                <Dropdown.Item
                  onClick={() => {
                    setDeleteAdminLevelData({
                      admin_level_id: original?.admin_level_id,
                      showDeletePopup: true
                    });
                  }}
                >
                  <span className="d-flex fs-16 font-normal p-0">
                    <p className="mb-0">
                      {capitalizeFirstLetter(t('general.common_text.text_14'))}
                    </p>
                  </span>
                </Dropdown.Item>
              </ConditionalRender>
            </Dropdown.Menu>
          </Dropdown>
        </ConditionalRender>
      )
    }
  ];

  const table = useAdvanceTable({
    data: allAdminLevel || [],
    columns: columns,
    selection: false,
    sortable: true,
    pageSize: limit,
    pagination: true,
    sorting: sorting,
    setSorting: setSorting
  });

  return (
    <Fragment>
      <div className="d-flex justify-content-between mb-2 align-items-center flex-column flex-sm-row">
        <div className="w-auto">
          <h3>{capitalizeFirstLetter(t('admin_level.title'))} </h3>
        </div>
        <div className="d-flex gap-2 w-auto flex-column flex-sm-row">
          <SearchBox
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          {/* <ConditionalRender
            condition={checkAccess(
              [modules.ADMIN_LEVEL.create],
              userPermission
            )}
          >
            <StandardButton
              buttonText={capitalizeFirstLetter(t('admin_level.button.text_1'))}
              onClick={() => {
                navigate(ADMIN_LEVEL_FORM);
              }}
            />
          </ConditionalRender> */}
        </div>
      </div>
      <ConditionalRender condition={!isLoading} fallBack={<StandardLoader />}>
        <div className="px-4 py-1 px-lg-6 bg-body-emphasis  rounded border-top border-bottom border-left border-right border position-relative top-1">
          <AdvanceTableProvider {...table}>
            <AdvanceTable
              tableProps={{
                className: 'phoenix-table fs-9 p-0',
                responsive: true
              }}
              dataLength={allAdminLevel.length}
            />
            <ConditionalRender condition={totalPages > 0}>
              <CustomAdvanceTableFooter
                pagination
                totalPages={totalPages}
                totalRecords={totalRecords}
                recordsPerPage={limit}
                currentPage={pageCount}
                setCurrentPage={setPageCount}
                CanPreviousPage={() => pageCount > 1}
                CanNextPage={() => totalPages > pageCount}
                handleNextPage={() => setPageCount(pageCount + 1)}
                handlePreviousPage={() => setPageCount(pageCount - 1)}
                showViewAllBtn={true}
                isAllVisibleData={isAllVisibleData}
                setIsAllVisibleData={setIsAllVisibleData}
                setRecordsPerPage={setLimit}
                isLoading={isLoading}
              />
            </ConditionalRender>
          </AdvanceTableProvider>
        </div>
      </ConditionalRender>
      {renderDeletePopup()}
      {renderViewAdminLevel()}
    </Fragment>
  );
};

export default AdminLevel;
